import { useFlywheel, type FlywheelActor } from '../lib/flywheel.js';

/**
 * Compact topbar indicator. Three nodes in a triangle, three arcs between
 * them. When a flywheel event fires, the originating node pulses and the
 * arc to its target lights up for ~1.8s.
 *
 * Click to open the full intro/closing view.
 */
interface Props {
  onClick: () => void;
}

const NODE_POSITIONS: Record<FlywheelActor, { cx: number; cy: number }> = {
  alumni: { cx: 12, cy: 8 },
  candidate: { cx: 36, cy: 8 },
  operator: { cx: 24, cy: 26 },
};

const ARC_PATHS: Array<{ from: FlywheelActor; to: FlywheelActor; d: string }> = [
  // alumni → candidate (top edge)
  { from: 'alumni', to: 'candidate', d: 'M 14 8 L 34 8' },
  // candidate → operator (right edge, slight curve)
  { from: 'candidate', to: 'operator', d: 'M 35 10 L 25 24' },
  // operator → alumni (left edge, slight curve)
  { from: 'operator', to: 'alumni', d: 'M 23 24 L 13 10' },
];

export function FlywheelIndicator({ onClick }: Props) {
  const { active } = useFlywheel();

  return (
    <button type="button" onClick={onClick} title="フライホイールを開く" className="flywheel-pill">
      <svg width="48" height="34" viewBox="0 0 48 34" aria-hidden>
        {ARC_PATHS.map((arc) => {
          const isActive =
            active && active.actor === arc.from && active.flowsTo === arc.to;
          return (
            <path
              key={`${arc.from}-${arc.to}`}
              d={arc.d}
              stroke={isActive ? 'var(--accent)' : 'var(--ink-muted)'}
              strokeWidth={isActive ? 1.6 : 0.8}
              strokeOpacity={isActive ? 1 : 0.4}
              fill="none"
              style={{ transition: 'stroke 0.3s, stroke-width 0.3s, stroke-opacity 0.3s' }}
            />
          );
        })}
        {(['alumni', 'candidate', 'operator'] as FlywheelActor[]).map((actor) => {
          const pos = NODE_POSITIONS[actor];
          const isActive = active?.actor === actor;
          return (
            <circle
              key={actor}
              cx={pos.cx}
              cy={pos.cy}
              r={isActive ? 4.2 : 3}
              fill={isActive ? 'var(--accent)' : 'var(--ink-muted)'}
              fillOpacity={isActive ? 1 : 0.6}
              style={{ transition: 'r 0.3s, fill 0.3s, fill-opacity 0.3s' }}
            />
          );
        })}
      </svg>
      <span>フライホイール</span>
    </button>
  );
}
